import React, { useState, useEffect } from "react";

import { API, graphqlOperation } from "aws-amplify";
import { countInventories } from "../../../graphql/inventory/custom-queries";
import {
  performUpdateAccountMini,
  syncableAccountUpdateProps,
} from "../../../graphql/account/accountCRUD";

import { DialogContentText, Tooltip } from "@material-ui/core";
import ArchiveIcon from "@material-ui/icons/Archive";

import { Button, Confirm, useNotify, useRedirect } from "react-admin";
import { useFormState } from "react-final-form";

import { RollbackIcon, UploadIcon } from "../../../components/common/iconography";
import {
  AccountStatus,
  ExternalStatus,
  InventoryStatus,
  ResourceType,
} from "../../../utils/constants";
import { logError, logHistory } from "../../../utils/appUtils";
import { triggerEvent, sanitizeZapierData } from "../../../utils/zapierUtils";
import { normalizeWebsiteUrl, setAccountSyncFailed } from "./AccountUtils";

const getInventory = async (record, setHasDependencies) => {
  try {
    debugger;
    let response = await API.graphql(
      graphqlOperation(countInventories, {
        filter: {
          reloadYardId: { eq: window.appUser?.reloadYardId },
          accountId: { eq: record.id },
          and: [
            { status: { ne: InventoryStatus.SHIPPED } },
            { status: { ne: InventoryStatus.ARCHIVED } },
          ],
        },
        limit: 1,
      })
    );
    const inventoryCount = response.data.searchInventorys.total;

    setHasDependencies(inventoryCount > 0);
  } catch (err) {
    logError("accounts:DeepArchiveButton:getInventory", err, false);
  }
};

const defaultMessage = (
  <DialogContentText>
    This action doesn’t delete any records and can be restored at any time.
    <br></br>
    Are you sure you want to archive this account?
  </DialogContentText>
);

const syncMessage = (
  <DialogContentText>
    This account is synced to an external system.<br></br>
    This action doesn't delete any records and can be restored locally, but an
    external system behavior may vary.
    <br></br>
    Are you sure you want to archive this account?
  </DialogContentText>
);

export const ArchiveRestoreButton = ({
  record,
  resource,
  classes,
  isDeleting,
  isSaving,
  setIsSaving,
  isSyncEnabled,
  isLocked,
  ...props
}) => {
  const notify = useNotify();
  const redirect = useRedirect();
  const [open, setOpen] = useState(false);
  const [hasDependencies, setHasDependencies] = useState(true);
  const { pristine } = useFormState({ subscription: { pristine: true } });
  const { id, active, status, businessName, displayName, externalId } = record;

  useEffect(() => {
    if (record.reloadYardId) getInventory(record, setHasDependencies);
  }, [record]);

  const handleSubmit = async () => {
    if (active) {
      if (hasDependencies) {
        notify("Account has active inventories.", "warning");
        return;
      } else setOpen(true);
    } else await handleArchive();
  };
  const handleDialogClose = () => setOpen(false);

  const handleArchive = async () => {
    setIsSaving(true);
    try {
      const newActiveState = active === "true" ? "false" : "true";
      const action = newActiveState === "true" ? "restored" : "archived";

      const accountToUpdate = {
        id,
        active: newActiveState,
        businessName,
        displayName,
        externalId,
      };

      if (isSyncEnabled) {
        accountToUpdate.externalStatus = ExternalStatus.SYNC_PENDING;
      }

      await performUpdateAccountMini(accountToUpdate);

      // Log history for the account archive/restore
      const historyNote = {
        parentId: id,
        parentType: ResourceType.ACCOUNT,
        text: `Account ${action}`,
      };
      logHistory(historyNote);

      if (isSyncEnabled) {
        try {
          const sanitizedData = sanitizeZapierData(
            syncableAccountUpdateProps,
            accountToUpdate
          );
          const normalizedData = {
            ...sanitizedData,
            website: normalizeWebsiteUrl(sanitizedData.website),
          };
          // we can't restore the account from the external system, so we don't need to trigger the event
          triggerEvent(normalizedData, "accountUpdated").catch((error) => {
            logError("accounts:ArchiveRestoreButton:triggerEvent", error);
            setAccountSyncFailed(accountToUpdate);
          });
        } catch (error) {
          setAccountSyncFailed(accountToUpdate);
          logError(
            "accounts:ArchiveRestoreButton:sanitizeZapierData",
            error,
            notify
          );
        }
      }

      notify(
        newActiveState === "true"
          ? `Account ${action} successfully`
          : `Account ${action} successfully`
      );
      redirect("/accounts");
    } catch (error) {
      logError(`accounts:DeepArchiveButton:handleArchive`, error, notify);
    } finally {
      setIsSaving(false);
      setOpen(false);
    }
  };

  const getButtonIcon = () => {
    if (isSyncEnabled) return <UploadIcon />;
    return record.active === "true" ? <ArchiveIcon /> : <RollbackIcon />;
  };

  const getButtonLabel = () => {
    return record.active === "true" ? "Archive" : "Restore";
  };

  const getDialogContent = () => {
    if (isSyncEnabled) return syncMessage;
    return defaultMessage;
  };

  if (status === AccountStatus.NEW) return null;
  if (!pristine || isLocked) {
    return (
      <Tooltip
        title={
          isLocked ? "Account is syncing." : "Save changes before archiving."
        }
      >
        <span>
          <Button
            {...props}
            label={getButtonLabel()}
            disabled
            className={classes.leftButtonSpacer}
          >
            {getButtonIcon()}
          </Button>
        </span>
      </Tooltip>
    );
  }
  return (
    <>
      <Button
        {...props}
        label={getButtonLabel()}
        color="default"
        disabled={isSaving || isDeleting || !pristine}
        className={classes.leftButtonSpacer}
        onClick={handleSubmit}
      >
        {getButtonIcon()}
      </Button>
      <Confirm
        isOpen={open}
        loading={isSaving}
        title="Archive Confirmation"
        onConfirm={handleArchive}
        content={getDialogContent()}
        onClose={handleDialogClose}
      />
    </>
  );
};
